import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import { cn } from '@/lib/utils'
import {
    FaltaCadastrada,
    FaltaInjustificada,
    FaltaJustificada,
} from '@/service/api/endpoints/faltas-cadastradas'
import { hoursToMinutes, minutesToHours } from '@acme/common'
import { StatusAprovacao, schema } from '@acme/core'
import { Chip } from '@mui/material'
import dayjs from 'dayjs'
import Menu from 'global/components/MenuV2'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Abono,
    Afastamento,
    CompensacaoFeriado,
    CompensacaoManual,
    Ocorrencia,
} from 'service/api'
import { match } from 'ts-pattern'
import { z } from 'zod'

interface IProps {
    dia: string
    abono?: Abono
    afastamento?: Afastamento & { Ocorrencia: Ocorrencia }
    compensacao?: CompensacaoFeriado
    compensacaoManual?: CompensacaoManual
    descontoDsr?: unknown
    feriado?: unknown
    folga?: z.infer<typeof schema.FolgaSchema>
    trocaDiaTrabalho?: {
        id: number
        folga: string
    }
    faltaJustificada?: FaltaJustificada & { readonly?: boolean }
    faltaInjustificada?: FaltaInjustificada & { readonly?: boolean }
}

type OcorrenciaOfProps = Omit<IProps, 'dia' | 'feriado'>

export default function Ausencias({
    row,
}: GridCellParams<OcorrenciaOfProps & { faltas: string }, IProps>) {
    const afastamento = (() => {
            if (
                row.afastamento &&
                row.afastamento.statusAprovacao !== StatusAprovacao.REPROVADO
            ) {
                return row.afastamento
            }
        })(),
        compensacao = (() => {
            if (row.compensacao && !row.feriado) return row.compensacao
            if (
                row.compensacao &&
                row.feriado &&
                dayjs(row.compensacao.dataCompensacao).isSame(
                    dayjs(row.compensacao.dataFeriado),
                    'day',
                )
            ) {
                return row.compensacao
            }
        })(),
        compensacaoManual = (() => {
            if (row.compensacaoManual) return row.compensacaoManual
        })(),
        descontoDsr = row.descontoDsr,
        folga = (() => {
            if (!afastamento && row.folga) return row.folga
        })(),
        trocaDiaTrabalho = (() => {
            if (!folga && row.trocaDiaTrabalho) return row.trocaDiaTrabalho
        })(),
        abono = (() => {
            if (
                row.abono &&
                row.abono.statusAprovacao !== StatusAprovacao.REPROVADO
            )
                return row.abono
        })()

    const ausencias: OcorrenciaOfProps = {
        afastamento,
        abono,
        compensacao,
        compensacaoManual,
        descontoDsr,
        folga,
        trocaDiaTrabalho,
        ...arrangeFaltasCadastradas(row as any),
    }

    const isPeriodo =
            !!afastamento &&
            !dayjs
                .utc(afastamento.dataFinal)
                .isSame(afastamento.dataInicial, 'day'),
        isInicio =
            isPeriodo &&
            row.dia === dayjs.utc(afastamento.dataInicial).format('YYYY-MM-DD'),
        isFim =
            isPeriodo &&
            row.dia === dayjs.utc(afastamento.dataFinal).format('YYYY-MM-DD')

    switch (Object.values(ausencias).filter(Boolean).length) {
        case 0:
            return <></>
        case 1:
            return (
                <ChipAusencia
                    {...{ ausencias, variant: 'chip' }}
                    className={cn({
                        'rounded-b-none mb-0 self-end h-7/10': isInicio,
                        'rounded-t-none mt-0 self-start h-7/10': isFim,
                        'h-full rounded-none': isPeriodo && !isInicio && !isFim,
                        'h-3/5': !isPeriodo,
                    })}
                />
            )
        default:
            return (
                <AusenciasMultiplas
                    {...{ ausencias, isFim, isInicio, isPeriodo }}
                />
            )
    }
}

interface PeriodoProps {
    isPeriodo: boolean
    isInicio: boolean
    isFim: boolean
}

function AusenciasMultiplas({
    ausencias,
    isFim,
    isInicio,
    isPeriodo,
}: IPropsChip & PeriodoProps) {
    const rearranged = Object.fromEntries(
        Object.entries(ausencias)
            .filter(([_, v]) => !!v)
            .sort(([keyA, valueA], [keyB, valueB]) => {
                if (
                    (keyA === 'faltaInjustificada' ||
                        keyA === 'faltaJustificada') &&
                    (keyB === 'faltaInjustificada' ||
                        keyB === 'faltaJustificada')
                ) {
                    const A = (valueA as any)?.readonly
                    const B = (valueB as any)?.readonly

                    if (A && !B) return 1
                    if (!A && B) return -1
                    return 0
                }
                return 0
            }),
    )

    const ausenciasPrincipal = Object.fromEntries([
        Object.entries(rearranged).find(([_, v]) => !!v)!,
    ])

    const outrasAusencias = Object.fromEntries(
        Object.entries(rearranged).filter(
            ([k, v]) => !!v && k !== Object.keys(ausenciasPrincipal)[0],
        ),
    )

    return [
        <ChipAusencia
            key="principal"
            ausencias={ausenciasPrincipal as OcorrenciaOfProps}
            className={cn({
                'rounded-b-none rounded-r-none mb-0 self-end h-7/10': isInicio,
                'rounded-t-none rounded-r-none mt-0 self-start h-7/10': isFim,
                'h-full rounded-none': isPeriodo && !isInicio && !isFim,
                'h-3/5': !isPeriodo,
            })}
        />,
        <Menu.Root
            arrow
            key="adicional"
            className={cn('w-fit h-3/5', {
                '*:rounded-b-none *:rounded-l-none mb-0 self-end h-7/10':
                    isInicio,
                '*:rounded-t-none *:rounded-l-none mt-0 self-start h-7/10':
                    isFim,
                'h-full *:rounded-none': isPeriodo && !isInicio && !isFim,
                'ml-1': !isPeriodo,
            })}
        >
            <Menu.Trigger>
                <Chip
                    label={`+${Object.values(outrasAusencias).length}`}
                    className={cn(
                        'w-fit h-full shadow-md font-bold border-foreground',
                        {
                            'border-y-2': isPeriodo && !isInicio && !isFim,
                            'border-t-2': isFim,
                            'border-b-2': isInicio,
                        },
                    )}
                />
            </Menu.Trigger>
            <Menu.List>
                {Object.entries(outrasAusencias).map(([k, v]) => (
                    <React.Fragment key={k}>
                        <ChipAusencia
                            ausencias={{ [k]: v } as OcorrenciaOfProps}
                            variant="item"
                        />
                    </React.Fragment>
                ))}
            </Menu.List>
        </Menu.Root>,
    ]
}

interface IPropsChip {
    ausencias: OcorrenciaOfProps
    variant?: 'chip' | 'item'
    className?: string
}
function ChipAusencia({ ausencias, variant, className }: IPropsChip) {
    const navigate = useNavigate()
    const { PENDENTE, PENDENTE_EXCLUSAO } = StatusAprovacao

    const {
        abono,
        afastamento,
        compensacao,
        compensacaoManual,
        descontoDsr,
        folga,
        trocaDiaTrabalho,
        faltaJustificada,
        faltaInjustificada,
    } = ausencias

    let componentProps: {
        label: string
        title: string
        href?: string
        status: Afastamento['statusAprovacao']
    } = {} as any
    switch (true) {
        case !!afastamento:
            componentProps = {
                label: afastamento.Ocorrencia.descricao,
                title:
                    afastamento.Ocorrencia.descricao +
                    match(afastamento.statusAprovacao)
                        .with(PENDENTE, () => ' (PENDENTE)')
                        .with(PENDENTE_EXCLUSAO, () => ' (EXCLUSÃO PENDENTE)')
                        .otherwise(() => ''),
                href: `afastamento/detalhes/${afastamento.id}`,
                status: afastamento.statusAprovacao,
            }
            break
        case !!compensacao:
            const dia = dayjs.utc(compensacao.dataFeriado)
            componentProps = {
                label: `Compensação: ${dia.format('DD/MM/YY')}`,
                title: `Compensação para o feriado do dia ${dia.format(
                    'DD/MM/YYYY',
                )}`,
                href: `compensacao-feriado/detalhes/${compensacao.id}`,
                status: dia.isSame(dayjs.utc(compensacao.dataFeriado), 'day')
                    ? 'REPROVADO'
                    : 'APROVADO',
            }
            break
        case !!compensacaoManual:
            const tipoLabel =
                compensacaoManual.tipo === 'COMPENSAR'
                    ? 'Compensar'
                    : 'Compensado'
            const destinos = compensacaoManual.Destinos?.length || 0
            componentProps = {
                label: `Comp. Manual: ${tipoLabel}`,
                title: `Compensação Manual - ${tipoLabel} - ${destinos} destino(s)`,
                href: `compensacao-manual/detalhes/${compensacaoManual.id}`,
                status: 'APROVADO',
            }
            break
        case !!abono:
            componentProps = {
                label: `Abono: ${abono.quantidadeHoras}`,
                title:
                    `Abono de ${abono.quantidadeHoras} horas` +
                    match(abono.statusAprovacao)
                        .with(PENDENTE, () => ' (PENDENTE)')
                        .with(PENDENTE_EXCLUSAO, () => ' (EXCLUSÃO PENDENTE)')
                        .otherwise(() => ''),
                href: `abono/detalhes/${abono.id}`,
                status: abono.statusAprovacao,
            }
            break
        case !!descontoDsr:
            componentProps = {
                label: 'Desconto DSR',
                title: 'Desconto de DSR',
                href: undefined,
                status: 'APROVADO',
            }
            break
        case !!folga:
            componentProps = {
                label: 'Folga',
                title: 'Folga',
                href: undefined,
                status: 'APROVADO',
            }
            break
        case !!trocaDiaTrabalho:
            componentProps = {
                label: `Troca dia trabalho (${dayjs
                    .utc(trocaDiaTrabalho.folga)
                    .format('DD/MM')})`,
                title: 'Troca dia de trabalho',
                href: `troca-dia-trabalho/detalhes/${trocaDiaTrabalho.id}`,
                status: 'APROVADO',
            }
            break
        case !!faltaJustificada:
            const justificativa = faltaJustificada.justificativa
                ? `- ${faltaJustificada.justificativa}`
                : ''
            componentProps = {
                label: `Falta justificada ${
                    faltaJustificada.tempo ? `(${faltaJustificada.tempo})` : ''
                }`,
                title: `Falta justificada ${justificativa}`,
                href: !faltaJustificada.readonly
                    ? `falta/detalhes/${faltaJustificada.id}`
                    : undefined,
                status: 'APROVADO',
            }
            break
        case !!faltaInjustificada:
            const tempo = faltaInjustificada.tempo
                ? `(${faltaInjustificada.tempo})`
                : ''
            componentProps = {
                label: `Falta injustificada ${tempo}`,
                title: `Falta injustificada`,
                href: !faltaInjustificada.readonly
                    ? `falta/detalhes/${faltaInjustificada.id}`
                    : undefined,
                status: 'APROVADO',
            }
            break
        default:
            return null
    }

    return variant === 'item' ? (
        <Menu.Item
            href={componentProps.href}
            title={componentProps.title}
            className={cn(
                { 'cursor-not-allowed': !componentProps.href },
                { 'cursor-pointer': !!componentProps.href },
                className,
            )}
        >
            {(() => {
                switch (componentProps.status) {
                    case 'APROVADO':
                        return (
                            <div
                                className="bg-success size-4 rounded-full mr-2 cursor-help"
                                title="Aprovado"
                            />
                        )
                    case 'REPROVADO':
                        return (
                            <div
                                className="bg-error size-4 rounded-full mr-2 cursor-help"
                                title="Reprovado"
                            />
                        )
                    case 'PENDENTE':
                    case 'PENDENTE_EXCLUSAO':
                        return (
                            <div
                                className="bg-warning size-4 rounded-full mr-2 cursor-help"
                                title="Pendente"
                            />
                        )
                    default:
                        return null
                }
            })()}
            {componentProps.label}
        </Menu.Item>
    ) : (
        <Chip
            className={cn(
                'w-full shadow-md font-bold',
                'dark:text-black',
                { 'select-none cursor-not-allowed': !componentProps.href },
                { 'cursor-pointer': !!componentProps.href },
                {
                    'bg-success hover:bg-success/80':
                        componentProps.status === 'APROVADO',
                },
                {
                    'bg-error hover:bg-error/80':
                        componentProps.status === 'REPROVADO',
                },
                {
                    'bg-warning hover:bg-warning/80':
                        componentProps.status === 'PENDENTE' ||
                        componentProps.status === 'PENDENTE_EXCLUSAO',
                },
                className,
            )}
            label={componentProps.label}
            title={componentProps.title}
            onClick={
                componentProps.href
                    ? () => navigate(componentProps.href!)
                    : undefined
            }
        />
    )
}

export function arrangeFaltasCadastradas(row: {
    faltaCadastrada?: FaltaCadastrada
    faltas: string
}): {
    faltaJustificada?: OcorrenciaOfProps['faltaJustificada']
    faltaInjustificada?: OcorrenciaOfProps['faltaInjustificada']
} {
    if (!row.faltaCadastrada)
        return { faltaJustificada: undefined, faltaInjustificada: undefined }
    const isJustificada = !!row.faltaCadastrada.justificativa
    if (row.faltaCadastrada.tempo) {
        const diff =
            hoursToMinutes(row.faltas) -
            hoursToMinutes(row.faltaCadastrada.tempo)

        if (diff > 0)
            return {
                [isJustificada ? 'faltaInjustificada' : 'faltaJustificada']: {
                    ...row.faltaCadastrada,
                    tempo: minutesToHours(diff),
                    justificativa: isJustificada
                        ? undefined
                        : 'Falta automática',
                    readonly: true,
                },
                [isJustificada ? 'faltaJustificada' : 'faltaInjustificada']:
                    row.faltaCadastrada,
            }
    }

    return {
        [isJustificada ? 'faltaJustificada' : 'faltaInjustificada']: {
            ...row.faltaCadastrada,
            tempo: undefined,
        },
        [isJustificada ? 'faltaInjustificada' : 'faltaJustificada']: undefined,
    }
}
